import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import {
  useUserIsEligibleForAllAccessFreeTrialQuery,
  useUserIsEligibleForStudioAiFreeTrialQuery,
} from '@repo/cf-user/hooks/use-user-data'
import { useUserBillingDetailsQuery } from '@repo/cf-user/gen/user'
import { useIsLoggedInQuery } from 'auth/hooks'
import { defaultLanguage } from 'cf-i18n/constants'
import { useLangCookie } from 'cf-i18n/cookies'
import type { UseCase } from 'cf-types'
import * as React from 'react'
import type { Language } from '../../gen/types'
import i18n from '../../i18n/i18n.json'
import { usePaymentsAppType, getModalLocation } from '../../components/payments-app-type'
import { mapUserToPaymentData } from '../lib/map-user-to-payments-data'
import { decidePlans, getDefaultSubscriptionType } from '../lib/plans'
import type { DefaultPlan, Plan } from '../lib/plans'
import type { PaymentsData } from './payments-dialog-types'

interface PaymentsDialogConfigContextValue {
  locale: Language
  i18n: typeof i18n.en
  plans: Plan[]
  useCase: UseCase
  promo?: string
  initialPaymentsData: PaymentsData
  defaultSubscriptionType: Plan['id']
  isEligibleForAllAccessFreeTrial: boolean
  isUserBillingDetailsPending: boolean
}

export const PaymentsDialogConfigContext = React.createContext<PaymentsDialogConfigContextValue>(
  null!,
)

export type { PaymentsDialogConfigContextValue }

export function PaymentsDialogConfigProvider({
  children,
  useCase = 'purchase',
  promo,
  defaultPlan,
}: {
  children: React.ReactNode
  useCase?: UseCase
  promo?: string
  defaultPlan: DefaultPlan
}) {
  const [langCookie] = useLangCookie()
  const locale = (langCookie || defaultLanguage) as Language
  const translations = i18n[locale]
  const hasSentAmplitudeEvent = React.useRef(false)

  const { data: userBillingDetails, isPending: isUserBillingDetailsPending } =
    useUserBillingDetailsQuery()
  const isLoggedInQuery = useIsLoggedInQuery()
  const isEligibleForAllAccessFreeTrialQuery = useUserIsEligibleForAllAccessFreeTrialQuery()
  const isEligibleForStudioAiFreeTrialQuery = useUserIsEligibleForStudioAiFreeTrialQuery()

  const plans = React.useMemo(
    () =>
      decidePlans({
        isEligibleForAllAccessFreeTrial: isEligibleForAllAccessFreeTrialQuery.data,
        isEligibleForStudioAiFreeTrial: isEligibleForStudioAiFreeTrialQuery.data,
        defaultPlan,
      }),
    [
      defaultPlan,
      isEligibleForAllAccessFreeTrialQuery.data,
      isEligibleForStudioAiFreeTrialQuery.data,
    ],
  )

  const initialPaymentsData = React.useMemo(
    () => mapUserToPaymentData(userBillingDetails),
    [userBillingDetails],
  )

  const isEligibleForAllAccessFreeTrial =
    ((useCase === 'purchase' || useCase === 'payment-button') &&
      Boolean(isEligibleForAllAccessFreeTrialQuery.data)) ||
    (isLoggedInQuery.isFetched && !isLoggedInQuery.data)

  const appType = usePaymentsAppType()

  React.useEffect(() => {
    if (hasSentAmplitudeEvent.current || useCase === 'payment-button') {
      return
    }

    if (isLoggedInQuery.isFetched) {
      hasSentAmplitudeEvent.current = true
      sendAmplitudeEvent(AmplitudeEvents.CLICKED_PAYMENT_MODAL, {
        location: getModalLocation(appType),
        logged_in: Boolean(isLoggedInQuery.data),
        useCase,
      })
    }
  }, [appType, isLoggedInQuery.data, isLoggedInQuery.isFetched, useCase])

  return (
    <PaymentsDialogConfigContext.Provider
      value={{
        locale,
        i18n: translations,
        plans,
        useCase,
        promo,
        initialPaymentsData,
        defaultSubscriptionType: getDefaultSubscriptionType(plans),
        isEligibleForAllAccessFreeTrial,
        isUserBillingDetailsPending,
      }}
    >
      {children}
    </PaymentsDialogConfigContext.Provider>
  )
}

export function usePaymentsDialogConfigContext() {
  return React.useContext(PaymentsDialogConfigContext)
}
